<template>
  <div>
    <div v-if="loading && !proxy" class="py-6 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading domains…</span>
    </div>
    <v-alert v-else-if="error" type="error" border="left" dense role="alert">
      {{ error }}
      <div class="mt-2"><gg-btn small tile outlined color="primary" @click="load">Retry</gg-btn></div>
    </v-alert>

    <!-- Not installed: new Docker servers get the proxy automatically; this is
         the fallback for servers built before that (or if it failed to start). -->
    <template v-else-if="proxy && !proxy.installed">
      <div class="d-flex align-start mb-3">
        <v-icon size="36" color="primary" class="mr-3" aria-hidden="true">mdi-web-check</v-icon>
        <div>
          <div class="body-1 font-weight-medium">Put your apps on real domains, with HTTPS</div>
          <p class="kvm-help mb-0">
            Visitors use <span class="kvm-dk-mono">https://blog.example.com</span> instead of an IP address and port,
            with a free, auto-renewing certificate.
            <template v-if="proxy.free_domain">
              No domain? Every app can get a free one like <span class="kvm-dk-mono">blog.{{ proxy.free_domain }}</span>.
            </template>
            This server needs a one-time setup first.
          </p>
        </div>
      </div>
      <div class="d-flex flex-wrap align-start" style="max-width: 560px;">
        <v-text-field v-model.trim="installEmail" label="Email for certificate notices" type="email" outlined dense
                      hint="Used for your HTTPS certificates (e.g. expiry notices). Not shared." persistent-hint class="mr-2" />
        <gg-btn tile color="primary" class="mt-1 kvm-dk-primary" :loading="installing" :disabled="!installEmail" @click="install">
          Set up domains
        </gg-btn>
      </div>
      <p class="kvm-help text-caption mt-2 mb-0">Ports 80 and 443 must be free — if an app already uses one of them, move it to another port first.</p>
      <v-alert v-if="installError" type="warning" dense text class="mt-3 mb-0" role="alert">{{ installError }}</v-alert>
    </template>

    <!-- Installed -->
    <template v-else-if="proxy">
      <div class="d-flex flex-wrap align-center mb-3">
        <v-chip small label :color="proxy.running ? 'green lighten-3' : 'grey lighten-2'" text-color="grey darken-4" class="mr-2">
          {{ proxy.running ? 'Running' : 'Stopped' }}
        </v-chip>
        <span class="kvm-help text-caption mr-auto">
          Certificates for {{ proxy.email }}<template v-if="proxy.free_domain"> · free domain: <span class="kvm-dk-mono">*.{{ proxy.free_domain }}</span></template>
        </span>
        <gg-btn small tile outlined color="primary" class="mr-2 mb-1" :loading="loading" @click="load">
          <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Refresh
        </gg-btn>
        <gg-btn small tile color="primary" class="mb-1 kvm-dk-primary" @click="openAdd">
          <v-icon left small aria-hidden="true">mdi-plus</v-icon>Add domain
        </gg-btn>
      </div>

      <p v-if="!proxy.routes.length" class="kvm-help my-6 text-center">No domains yet. Add one to publish an app.</p>
      <v-simple-table v-else dense class="kvm-dk-table">
        <caption class="kvm-sr-only">Domains</caption>
        <thead>
          <tr>
            <th scope="col">Domain</th>
            <th scope="col">Goes to</th>
            <th scope="col">DNS</th>
            <th scope="col" class="text-right"><span class="kvm-sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in proxy.routes" :key="r.hostname + r.path">
            <td data-label="Domain">
              <a :href="routeUrl(r)" target="_blank" rel="noopener" class="kvm-dk-link">{{ routeLabel(r) }}</a>
            </td>
            <td data-label="Goes to">{{ targetLabel(r.port) }}</td>
            <td data-label="DNS">
              <span v-if="r.dns_ok === true" class="kvm-dk-ok"><v-icon small class="kvm-dk-ok" aria-hidden="true">mdi-check-circle</v-icon> OK</span>
              <span v-else-if="r.dns_ok === false" class="kvm-dk-warn" :title="'Point an A record for this name to ' + proxy.server_ip">
                <v-icon small class="kvm-dk-warn" aria-hidden="true">mdi-alert</v-icon> Not pointing here yet
              </span>
              <span v-else class="kvm-help">—</span>
            </td>
            <td data-label="Actions" class="text-right">
              <gg-btn icon small :aria-label="'Remove ' + routeLabel(r)" :disabled="removing === routeLabel(r)" @click="removeRoute(r)">
                <v-icon small color="error">mdi-delete-outline</v-icon>
              </gg-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <p v-if="proxy.routes.some(r => r.dns_ok === false)" class="kvm-help text-caption mt-2 mb-0">
        For your own domains, add a DNS <strong>A record</strong> pointing to <span class="kvm-dk-mono">{{ proxy.server_ip }}</span>.
        HTTPS starts working a minute or two after DNS updates.
      </p>
    </template>

    <!-- Add domain -->
    <v-dialog v-model="add.open" max-width="560" persistent>
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-add-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-add-title" class="body-1 font-weight-medium mb-3">Add a domain</h3>
          <v-radio-group v-model="add.kind" row dense hide-details class="mt-0 mb-3">
            <v-radio v-if="proxy && proxy.free_domain" label="Free subdomain" value="free" />
            <v-radio label="One of my domains" value="mine" />
            <v-radio label="Another domain" value="own" />
          </v-radio-group>
          <v-text-field v-if="add.kind === 'free'" v-model.trim="add.name" label="Subdomain" placeholder="blog" outlined dense
                        :suffix="'.' + (proxy && proxy.free_domain)" persistent-hint class="mb-2"
                        hint="Each app gets its own address, so several apps can share this server." />
          <kvm-own-domain v-else-if="add.kind === 'mine'" v-model="add.mine" :server-ip="proxy && proxy.server_ip" />
          <v-text-field v-else v-model.trim="add.hostname" label="Domain" placeholder="blog.example.com" outlined dense persistent-hint
                        :hint="'Point its DNS A record to ' + (proxy && proxy.server_ip) + ' for HTTPS to work.'" class="mb-2" />
          <v-text-field v-model.trim="add.path" label="Path" placeholder="/" outlined dense persistent-hint class="mb-2"
                        hint="/ is the whole domain. Use a path like /blog to put several apps on one domain (the app must support running under a path)." />
          <v-combobox v-model="add.port" :items="portItems" item-text="text" item-value="value" :return-object="false"
                      label="App" outlined dense persistent-hint
                      hint="Pick a container's published port, or type a port number" class="mb-2" />
          <v-alert v-if="add.error" type="warning" dense text class="mb-0" role="alert">{{ add.error }}</v-alert>
          <div class="text-right mt-3">
            <gg-btn text class="mr-2" :disabled="add.saving" @click="add.open = false">Cancel</gg-btn>
            <gg-btn tile color="primary" class="kvm-dk-primary" :loading="add.saving" :disabled="!addHostname || !add.port" @click="saveRoute">Add</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'
import { pointRecord } from '@/helpers/ownDomains'
import KvmOwnDomain from '@/views/services/hosting/kvm/KvmOwnDomain'

// "0.0.0.0:3001->3001/tcp, [::]:3001->3001/tcp" → [3001]
const hostPorts = (ports) => {
  const out = new Set()
  for (const m of String(ports || '').matchAll(/:(\d+)->\d+\/tcp/g)) out.add(Number(m[1]))
  return [...out]
}

export default {
  name: 'KvmDockerProxy',
  components: { KvmOwnDomain },
  props: {
    serviceId: { type: [String, Number], required: true },
    refreshKey: { type: Number, default: 0 },
  },
  data: () => ({
    proxy: null,
    containers: [],
    loading: false,
    error: '',
    installEmail: '',
    installing: false,
    installError: '',
    removing: '',
    add: { open: false },
  }),
  computed: {
    // Published web ports on this server (excluding the proxy's own 80/443).
    portItems () {
      const items = []
      for (const c of this.containers) {
        if (c.name === 'prox-proxy') continue
        for (const p of hostPorts(c.ports)) {
          if (p !== 80 && p !== 443) items.push({ text: `${c.name} — port ${p}`, value: p })
        }
      }
      return items
    },
    addHostname () {
      if (!this.add.open) return ''
      // Always a subdomain: the bare free name is the server's, not an app's.
      if (this.add.kind === 'free') return this.add.name ? `${this.add.name.toLowerCase()}.${this.proxy.free_domain}` : ''
      if (this.add.kind === 'mine') return this.add.mine && this.add.mine.entry ? this.add.mine.hostname : ''
      return (this.add.hostname || '').toLowerCase()
    },
  },
  watch: {
    refreshKey () { this.load() },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    async load () {
      this.loading = true
      try {
        const [proxy, containers] = await Promise.all([
          kvm.dockerProxy(this.serviceId),
          kvm.dockerContainers(this.serviceId).catch(() => []),
        ])
        this.proxy = proxy
        this.containers = containers
        this.error = ''
        // A previous attempt that didn't finish keeps the email — retry is one click.
        if (!proxy.installed && proxy.email && !this.installEmail) this.installEmail = proxy.email
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    targetLabel (port) {
      const hit = this.portItems.find(i => i.value === port)
      return hit ? hit.text : `port ${port}`
    },
    async install () {
      this.installing = true
      this.installError = ''
      try {
        const op = await kvm.dockerProxyInstall(this.serviceId, this.installEmail)
        this.$emit('operation', op)
      } catch (e) {
        this.installError = e.data && e.data.errors ? Object.values(e.data.errors).flat().join(' ') : e.message
      } finally {
        this.installing = false
      }
    },
    openAdd () {
      this.add = { open: true, kind: this.proxy.free_domain ? 'free' : 'own', name: '', hostname: '', mine: null, path: '/', port: null, saving: false, error: '' }
    },
    async saveRoute () {
      const a = this.add
      const port = Number(typeof a.port === 'object' && a.port ? a.port.value : a.port)
      if (!port) { a.error = 'Choose the app (or type its port number).'; return }
      if (!this.addHostname) { a.error = a.kind === 'free' ? 'Enter a subdomain, like blog.' : 'Enter the domain.'; return }
      if (a.kind === 'mine' && !(a.mine && a.mine.ready)) { a.error = (a.mine && a.mine.notReady) || 'Choose which of your domains to use.'; return }
      a.saving = true
      a.error = ''
      try {
        // Their own domain: its DNS record first (agreed to if it replaces one).
        if (a.kind === 'mine' && a.mine.route.kind !== 'manual') {
          try {
            await pointRecord(a.mine.route, a.mine.sub, this.proxy.server_ip)
          } catch (e) {
            a.error = `Couldn't set up ${a.mine.hostname}: ${e.message}`
            return
          }
        }
        const res = await kvm.dockerProxyAddRoute(this.serviceId, this.addHostname, port, a.path || '/')
        this.reportSuccess(res.applied
          ? `${this.routeLabel(res.route)} is live — HTTPS is set up automatically on the first visit.`
          : `${this.routeLabel(res.route)} saved; it goes live when the proxy starts.`)
        a.open = false
        this.load()
      } catch (e) {
        a.error = e.data && e.data.errors ? Object.values(e.data.errors).flat().join(' ') : e.message
      } finally {
        a.saving = false
      }
    },
    // "site.example.com" for the whole domain, "site.example.com/blog" otherwise.
    routeLabel (r) {
      return r.hostname + (r.path && r.path !== '/' ? r.path : '')
    },
    routeUrl (r) {
      return 'https://' + r.hostname + (r.path && r.path !== '/' ? r.path + '/' : '/')
    },
    async removeRoute (r) {
      this.removing = this.routeLabel(r)
      try {
        await kvm.dockerProxyRemoveRoute(this.serviceId, r.hostname, r.path || '/')
        this.reportSuccess(`${this.routeLabel(r)} removed.`)
        this.load()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.removing = ''
      }
    },
  },
  mounted () {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-mono { font-family: monospace; font-size: 0.85em; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-link { @include kvm-text-green-aa; }
.kvm-dk-ok { color: $kvm-focus-light; .theme--dark & { color: $kvm-focus-dark; } }
.kvm-dk-warn { color: #806600; .theme--dark & { color: #ffb74d; } }
.kvm-dk-table {
  @include kvm-stacked-table;
  th { white-space: nowrap; }
}
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
